/* eslint-disable no-restricted-globals */
import { Grid, Stack, Typography } from "@mui/material"
import { Context, ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"

import {
    TT_DEVICE_CAT,
    TT_DEVICE_PHASES,
    TT_DEVICE_REF,
    TT_DEVICE_TYPE,
    TT_HARDWARE_VERSION,
    TT_MODEL_NUMBER,
    TT_REGISTERED_CAPACITY_TTR,
    TT_SOFTWARE_VERSION,
} from "../../../../../constants/tooltips"
import {
    registerDeviceLabelMapping,
    CORE_ADDITIONAL_COMMENTS_REG,
    CORE_CAPACITY_REG,
    CORE_CATEGORY_REG,
    CORE_HARDWARE_VERSION_REG,
    CORE_MODEL_REG,
    CORE_PHASES_REG,
    CORE_SOFTWARE_VERSION_REG,
    CORE_TYPE_REG,
} from "../../../../../constants/ttr/registerDevice"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import { reverseObject } from "../../../../../utils/general/generalUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import HelpIcon from "../../../../helper/helpIcon"
import ValidatedRadioGroup from "../../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import AppSkeleton from "../../../../loading/skeletons/appSkeleton"
import CoreDeviceDetailsSkeleton from "../../../../loading/skeletons/coreDeviceDetailsSkeleton"
import { AppCard, AppCardContent } from "../../../../surfaces/appCard"
import DisplayIfElse from "../../../../wrappers/displayIfElse"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { TTRDeviceFormData } from "../../types/providerTypes"

const mapRegisterNameToValues = (
    formData: TTRDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [CORE_ADDITIONAL_COMMENTS_REG]: formData.coreDetails.additionalComments,
    [CORE_CAPACITY_REG]: formData.coreDetails.registeredCapacity,
    [CORE_CATEGORY_REG]: formData.coreDetails.deviceCategory,
    [CORE_HARDWARE_VERSION_REG]: formData.coreDetails.hardwareVersion,
    [CORE_MODEL_REG]: formData.coreDetails.model,
    [CORE_PHASES_REG]: formData.coreDetails.numberOfPhases,
    [CORE_SOFTWARE_VERSION_REG]: formData.coreDetails.softwareVersion,
    [CORE_TYPE_REG]: formData.coreDetails.deviceType,
})

interface ICoreDeviceDetailsProps {
    context: Context<IDeviceFormContextValue<TTRDeviceFormData>>
}

/** Displays a form page that allows the user to enter the core
 * information about a device the want to publish
 */
const CoreDeviceDetails = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        formData,
        setFormData,
        deviceRef,
        formIsLoading,
        saveDraft,
        saveDraftLoading,
        errors: contextErrors,
    } = useContext(context)

    const isFirstPage = currentIndex === 0

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: mapRegisterNameToValues(formData),
        values: mapRegisterNameToValues(formData),
        shouldUnregister: true,
        mode: "all",
    })

    const {
        deviceCategoryIds,
        deviceTypeIds,
        deviceCategoryMappings,
        deviceTypeMappings,
        devicePhaseMappings,
        isLoading: dropDownsLoading,
    } = useFilterReferenceMappings()

    const MAX_COMMENT_CHARACTERS = 500

    const reverseDevicePhaseMappings = reverseObject(devicePhaseMappings)

    const getUpdatedFormData = (): TTRDeviceFormData => ({
        ...formData,
        coreDetails: {
            manufacturer: formData.coreDetails.manufacturer,
            model: watch(CORE_MODEL_REG) as string,
            deviceCategory: watch(CORE_CATEGORY_REG) as number,
            deviceType: watch(CORE_TYPE_REG) as number,
            registeredCapacity: watch(CORE_CAPACITY_REG) as number,
            numberOfPhases: watch(CORE_PHASES_REG) as number,
            softwareVersion: watch(CORE_SOFTWARE_VERSION_REG) as string,
            hardwareVersion: watch(CORE_HARDWARE_VERSION_REG) as string,
            additionalComments: watch(CORE_ADDITIONAL_COMMENTS_REG) as string,
        },
    })

    const updateCoreDeviceDetails = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
    }

    const onClickBack = isFirstPage
        ? undefined
        : (): void => {
              updateCoreDeviceDetails()
              setCurrentIndex(currentIndex - 1)
          }

    const onClickNext = (): void => {
        updateCoreDeviceDetails()
        setCurrentIndex(currentIndex + 1)
    }

    const onSaveDraft = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
        saveDraft(updatedFormData)
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 1.5}>
                <FormHeader
                    header="Core Device Details"
                    subHeader="To start your submission, please enter the following core details about the device"
                    index={currentIndex + 1}
                />
                <AppCardContent>
                    <Grid container spacing={GRID_SPACING / 2}>
                        <Grid
                            item
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <Typography fontWeight={500} marginRight={1}>
                                Device Reference:
                            </Typography>
                            {deviceRef || <AppSkeleton width={200} />}
                            <HelpIcon tooltip={TT_DEVICE_REF} />
                        </Grid>
                    </Grid>
                </AppCardContent>
                <DisplayIfElse
                    condition={formIsLoading}
                    displayIfTrue={<CoreDeviceDetailsSkeleton />}
                    displayIfFalse={
                        <>
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={CORE_MODEL_REG}
                                label={
                                    registerDeviceLabelMapping[CORE_MODEL_REG]
                                }
                                placeholder="Enter device model name or number"
                                tooltip={TT_MODEL_NUMBER}
                            />
                            <ValidatedAutocomplete
                                control={control}
                                required
                                registerName={CORE_CATEGORY_REG}
                                label={
                                    registerDeviceLabelMapping[
                                        CORE_CATEGORY_REG
                                    ]
                                }
                                options={deviceCategoryIds}
                                getOptionLabel={(option) =>
                                    deviceCategoryMappings[option]
                                }
                                loading={dropDownsLoading}
                                tooltip={TT_DEVICE_CAT}
                            />
                            <ValidatedAutocomplete
                                control={control}
                                required
                                registerName={CORE_TYPE_REG}
                                label={
                                    registerDeviceLabelMapping[CORE_TYPE_REG]
                                }
                                options={deviceTypeIds}
                                getOptionLabel={(option) =>
                                    deviceTypeMappings[option]
                                }
                                loading={dropDownsLoading}
                                tooltip={TT_DEVICE_TYPE}
                            />
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={CORE_CAPACITY_REG}
                                label={
                                    registerDeviceLabelMapping[
                                        CORE_CAPACITY_REG
                                    ]
                                }
                                tooltip={TT_REGISTERED_CAPACITY_TTR}
                                placeholder="Enter a number"
                                customRules={{
                                    pattern: {
                                        value: /^\d+([.]\d+)?$/,
                                        message:
                                            "Capacity must be a positive number",
                                    },
                                }}
                            />
                            <ValidatedRadioGroup
                                control={control}
                                required
                                registerName={CORE_PHASES_REG}
                                label={
                                    registerDeviceLabelMapping[CORE_PHASES_REG]
                                }
                                radioOptions={reverseDevicePhaseMappings}
                                tooltip={TT_DEVICE_PHASES}
                            />
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={CORE_SOFTWARE_VERSION_REG}
                                label={
                                    registerDeviceLabelMapping[
                                        CORE_SOFTWARE_VERSION_REG
                                    ]
                                }
                                placeholder="Software version number"
                                tooltip={TT_SOFTWARE_VERSION}
                            />
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={CORE_HARDWARE_VERSION_REG}
                                label={
                                    registerDeviceLabelMapping[
                                        CORE_HARDWARE_VERSION_REG
                                    ]
                                }
                                placeholder="Hardware version number"
                                tooltip={TT_HARDWARE_VERSION}
                            />
                            <ValidatedTextField
                                control={control}
                                required={false}
                                registerName={CORE_ADDITIONAL_COMMENTS_REG}
                                label={
                                    registerDeviceLabelMapping[
                                        CORE_ADDITIONAL_COMMENTS_REG
                                    ]
                                }
                                placeholder={
                                    `Normally left blank but can be used to enter comments` +
                                    ` that may help users. Max ${MAX_COMMENT_CHARACTERS} characters`
                                }
                                customRules={{
                                    maxLength: {
                                        value: MAX_COMMENT_CHARACTERS,
                                        message: `Max permitted characters: ${MAX_COMMENT_CHARACTERS}`,
                                    },
                                }}
                                tooltip="Any additional comments/notes provided in this field will be visible to all users. Please use the comments functionality available post-submission to share any non-public comments with the evaluator, DNO or ENA users"
                                // Multiline is broken in Material UI: https://github.com/mui/material-ui/issues/39105
                            />
                            <FormErrors
                                errors={errors}
                                getFriendlyName={(key: string) =>
                                    registerDeviceLabelMapping[key]
                                }
                            />
                        </>
                    }
                />
                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={() => handleSubmit(onClickNext)()}
                    nextText="Next"
                    allDisabled={formIsLoading}
                    onClickMiddle={onSaveDraft}
                    middleDisabled={saveDraftLoading}
                />
            </Stack>
        </AppCard>
    )
}

export default CoreDeviceDetails
